/**
 * Authentication Context
 * 
 * Provides authentication state and methods to all components.
 * Wraps the app to enable auth checks throughout the component tree.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, onAuthStateChange, signIn, signOut } from '../services/authService';

// Create context
const AuthContext = createContext({
    user: null,
    session: null,
    loading: true,
    signIn: async () => { },
    signOut: async () => { },
});

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    // Initialize auth state on mount
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const { user: currentUser, session: currentSession } = await getCurrentUser();
                setUser(currentUser);
                setSession(currentSession);
            } catch (error) {
                console.error('Error initializing auth:', error);
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();

        // Subscribe to auth state changes
        const subscription = onAuthStateChange((event, currentSession) => {
            console.log('Auth state changed:', event);
            setSession(currentSession);
            setUser(currentSession?.user || null);
            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => {
            subscription.unsubscribe();
        };
    }, []);

    // Sign in handler
    const handleSignIn = async (email, password) => {
        const { user: signedInUser, session: signedInSession, error } = await signIn(email, password);

        if (error) {
            return { error };
        }

        setUser(signedInUser);
        setSession(signedInSession);
        return { user: signedInUser, session: signedInSession, error: null };
    };

    // Sign out handler
    const handleSignOut = async () => {
        const { error } = await signOut();

        if (error) {
            return { error };
        }

        setUser(null);
        setSession(null);
        return { success: true, error: null };
    };

    // Context value
    const value = {
        user,
        session,
        loading,
        signIn: handleSignIn,
        signOut: handleSignOut,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
